import { useState, useEffect, useCallback } from 'react';
import FinancialRecordService from '../../../../services/financial_record_service';
import { useTenant } from '../../../../hooks/useTenant';
import { FINANCIAL_STATUS, FINANCIAL_TYPES, FINANCIAL_SOURCES } from '../../../../constants/financial';

const useMemberFinancials = (member) => {
    const { idTenant } = useTenant();
    const [financials, setFinancials] = useState([]);
    const [groupedFinancials, setGroupedFinancials] = useState([]);
    const [loading, setLoading] = useState(false);

    const refresh = useCallback(async () => {
        if (!member?.id || !idTenant) return;
        setLoading(true);
        try {
            const data = await FinancialRecordService.getByMember(idTenant, member.id);
            setFinancials(data);

            // Agrupa os registros por idSale
            const grouped = {};
            const resultList = [];

            data.forEach(record => {
                if (record.idSale) {
                    if (!grouped[record.idSale]) {
                        // Cria um registro sintético para representar a venda condensada
                        const groupRecord = {
                            ...record,
                            id: `group_${record.idSale}`, // previne colisões se não for único
                            isGroup: true,
                            netAmount: 0,
                            grossAmount: 0,
                            status: record.status, // Será ajustado abaixo
                            subItems: []
                        };
                        grouped[record.idSale] = groupRecord;
                        resultList.push(groupRecord);
                    }
                    grouped[record.idSale].subItems.push(record);
                    grouped[record.idSale].netAmount += (record.netAmount || 0);
                    grouped[record.idSale].grossAmount += (record.grossAmount || 0);

                    // Ajuste de status do grupo:
                    // - MEMBER OVERDUE/PENDING → afeta o grupo (cliente não pagou!)
                    // - ACQUIRER PENDING → NÃO afeta o grupo (cliente já pagou, aguarda repasse)
                    const currGroupStatus = grouped[record.idSale].status;
                    if (record.sourceType === FINANCIAL_SOURCES.MEMBER) {
                        if (record.status === FINANCIAL_STATUS.OVERDUE) {
                            grouped[record.idSale].status = FINANCIAL_STATUS.OVERDUE;
                        } else if (record.status === FINANCIAL_STATUS.PENDING && currGroupStatus === FINANCIAL_STATUS.PAID) {
                            grouped[record.idSale].status = FINANCIAL_STATUS.PENDING;
                        }
                    }
                    // Parcelas ACQUIRER PENDING não alteram o status consolidado
                } else {
                    resultList.push(record);
                }
            });

            setGroupedFinancials(resultList);

        } catch (error) {
            console.error("Erro ao carregar financeiro do membro:", error);
        } finally {
            setLoading(false);
        }
    }, [member?.id, idTenant]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Saldo devedor do CLIENTE: apenas registros com sourceType "MEMBER" (dívida real do aluno).
    const pendingDebt = financials.filter(f =>
        f.type === FINANCIAL_TYPES.INCOME &&
        (f.status === FINANCIAL_STATUS.PENDING || f.status === FINANCIAL_STATUS.OVERDUE) &&
        f.sourceType === FINANCIAL_SOURCES.MEMBER
    );
    const totalDebt = pendingDebt.reduce((acc, curr) => acc + (curr.netAmount || curr.grossAmount || 0), 0);

    return {
        financials: groupedFinancials,
        rawFinancials: financials,
        loading,
        pendingDebt,
        totalDebt,
        refresh
    };
};

export default useMemberFinancials;
